import type { JourneyCondition } from '../journey/steps.ts'
import { CHECK_ASSERTIONS, countsInstances, type JourneyCheck } from '../journey/check-types.ts'
import { describeMiss, flatTargetCandidates, flatTargetDefinitions, nonIdentityKeyStale, resolveDefinition, sourceDecidesKeys, type CanonicalTarget } from '../polish/sightmap/resolve-definition.ts'
import { z } from 'zod'
import { flattenComponents, selectorsOf, viewMatchesSurfaceId, type SightmapDoc } from '../polish/sightmap/schema.ts'

export const COMPONENT_PATH = z.array(z.object({
  componentId: z.string().min(1), params: z.record(z.string()).optional(),
}).strict()).min(1)
export const SIGHTMAP_TARGET = z.object({ surfaceId: z.string().min(1), path: COMPONENT_PATH, selector: z.string().trim().min(1).optional(), paramsSource: z.literal('observation').optional(), observationRef: z.string().min(1).optional() }).strict()
// controlId is read compatibility for existing catalogs. New bindings carry the complete path.
export const COMPONENT_TARGET = z.object({
  surfaceId: z.string().min(1), controlId: z.string().min(1).optional(), path: COMPONENT_PATH.optional(), selector: z.string().trim().min(1).optional(), paramsSource: z.literal('observation').optional(), observationRef: z.string().min(1).optional(),
}).refine(target => !!target.path?.length || !!target.controlId, 'A Sightmap target needs a component path')
// Fresh conditions share the same complete targets as actions and checks. Persisted schemas
// still read selector conditions so conversion can bind historical evidence without dropping gates.
export const AUTHORED_CONDITION: z.ZodType<JourneyCondition> = z.lazy(() => z.union([
  z.object({ type: z.literal('route'), operator: z.enum(['matches', 'not_matches']), pattern: z.string() }).strict(),
  z.object({ type: z.literal('component'), operator: z.enum(['exists', 'not_exists']), component: SIGHTMAP_TARGET }).strict(),
  z.object({ type: z.enum(['all', 'any']), conditions: z.array(AUTHORED_CONDITION).min(1) }).strict(),
  z.object({ type: z.literal('not'), condition: AUTHORED_CONDITION }).strict(),
]))
export type ComponentTarget = z.infer<typeof COMPONENT_TARGET>
/** Observation handles are capabilities for one captured page, never shared tool identity. */
export function durableTarget(target: ComponentTarget): ComponentTarget {
  const { observationRef: _observationRef, ...durable } = target
  return durable
}
/** The only target equality key: the address without its observation handle. When the map resolves the
 * target, the address of its canonical form, so an alias and its current id name one referent.
 * Unresolved targets keep their raw address. */
export function canonicalKey(target: ComponentTarget, doc: SightmapDoc | null): string {
  const { observationRef: _observationRef, ...address } = target
  const resolution = doc ? resolveDefinition(address, doc) : null
  return resolution?.kind === 'unique' ? addressKey(canonicalForm(address, resolution.hit), sourceDecidesKeys(resolution.hit)) : addressKey(address, false)
}
export function sameTarget(a: ComponentTarget | undefined, b: ComponentTarget | undefined, doc: SightmapDoc | null): boolean {
  if (!a || !b) return a === b
  return canonicalKey(a, doc) === canonicalKey(b, doc)
}
/** Current surface and definition ids, the reference's params and the control selector. */
function canonicalForm(target: ComponentTarget, hit: CanonicalTarget): ComponentTarget {
  return {
    surfaceId: hit.surfaceId,
    path: hit.path.map((definition, i) => {
      const { componentId, params } = hit.reference.path[i]!
      return { componentId: definition.id ?? componentId, ...(params && Object.keys(params).length ? { params } : {}) }
    }),
    ...(target.selector ? { selector: target.selector } : {}),
    ...(target.paramsSource ? { paramsSource: target.paramsSource } : {}),
  }
}
// `paramsSource` decides how a `$` token in a param reads and, where `sourceDecides`, whether a key is dropped or
// stale, so it counts only there: dedupe never merges forms that resolve differently.
// Serialized with sorted keys: this module also runs in the browser bundle, which has no `node:util`.
function addressKey(target: ComponentTarget, sourceDecides: boolean): string {
  const { paramsSource, ...durable } = target
  const address = paramsSource && (sourceDecides || JSON.stringify(durable.path ?? []).includes('$')) ? { ...durable, paramsSource } : durable
  return JSON.stringify(address, (_key, value: unknown) => value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).sort(([x], [y]) => x < y ? -1 : x > y ? 1 : 0)) : value)
}
// TargetRule: the one statement of which steps and checks need a target, and what identifies one.
/** Action types that act on one element. Each needs a target. */
export const TARGETED_ACTION_TYPES = ['click', 'hover', 'mousemove', 'type', 'input', 'select', 'drag', 'dragTo', 'uploadFile', 'dropFile', 'scrollIntoView'] as const
/** Action types that act on a target only when they name one. */
export const OPTIONALLY_TARGETED_ACTION_TYPES = ['pressKey', 'read_variable'] as const
const TARGETED = new Set<string>(TARGETED_ACTION_TYPES)
const UNTARGETED_CHECKS = new Set<string>(['abstract', 'reachSurfaceCheck', 'url_contains'])
const ELEMENT_CHECK_ASSERTIONS = CHECK_ASSERTIONS.filter(assertion => !UNTARGETED_CHECKS.has(assertion))
const TARGET_FIX = 'set "label" to a discovered interaction label, or "component" to the observed Sightmap hierarchy {surfaceId, path}; when neither identifies the element, make it "abstract"'
export const TARGET_RULE = [
  `Steps of type ${TARGETED_ACTION_TYPES.join(', ')} act on one element and need its target; ${OPTIONALLY_TARGETED_ACTION_TYPES.join(' and ')} act on a target only when they name one.`,
  `Checks with assertion ${ELEMENT_CHECK_ASSERTIONS.join(', ')} need a Sightmap target.`,
  'A target is "label", a discovered interaction label, or "component", the observed Sightmap hierarchy {surfaceId, path}. A check label must be an interaction label listed with a Sightmap target.',
  'No other field identifies an element: no raw selector, no text matching of the action description. When neither identifies the element, make the step or check "abstract".',
].join(' ')
/** Whether an action type may name a target. */
export function takesTarget(type: string): boolean {
  return TARGETED.has(type) || (OPTIONALLY_TARGETED_ACTION_TYPES as readonly string[]).includes(type)
}
interface TargetedAction {
  type: string; action: string; condition?: JourneyCondition; keyboardEvent?: string; selector?: string; label?: string; surfaceId?: string; controlId?: string; controlName?: string; component?: ComponentTarget; destination?: ComponentTarget; value?: string; inRunAnchors?: unknown
}
/** A component target, or a recorded surfaceId+controlId pair. */
export function carriesComponentIdentity(action: Pick<TargetedAction, 'component' | 'surfaceId' | 'controlId'>): boolean {
  return !!action.component || !!(action.surfaceId && action.controlId)
}
export function needsComponent(action: TargetedAction): boolean {
  return TARGETED.has(action.type) || (action.type === 'read_variable' && !!(action.selector || action.component)) || (action.type === 'pressKey' && action.keyboardEvent !== 'keyup' && !!(action.selector || action.component))
}
/** The authoring problem of an action that needs a target and carries no target identity. */
export function missingActionTarget(action: TargetedAction): string | null {
  return needsComponent(action) && !carriesComponentIdentity(action) ? `action "${action.action}" (${action.type}) has no target: ${TARGET_FIX}` : null
}

/** The authoring problem of an element check without a Sightmap target. */
export function missingCheckTarget(check: JourneyCheck): string | null {
  if (UNTARGETED_CHECKS.has(check.assertion) || SIGHTMAP_TARGET.safeParse(check.component).success) return null
  const label = check.label ? `: label ${check.label} has no observed Sightmap component` : ''
  return `check "${check.description}" (assertion ${check.assertion}) has no Sightmap target${label}; ${TARGET_FIX}`
}

/** Convert an interaction the Sightmap cannot identify into a live-resolved abstract step, keeping its
 * recorded selector as the author-time witness. An abstract step never executes that selector — the
 * live resolver owns control choice — but the runner's authored-target guard reads it to reject a
 * substitute control on any page the recorded control never lived on, so dropping it would leave the
 * resolver free to click a lookalike on an unrelated surface. Strip only the identity evidence the map
 * could not verify (component, control and Surface identities, and the observation-local label). */
export function resolveAsAbstract<T extends TargetedAction>(action: T): T {
  const { component: _component, surfaceId: _surfaceId, controlId: _controlId, controlName: _controlName, label: _label, ...rest } = action
  return { ...rest, type: 'abstract' } as T
}

/** Convert authoring evidence into an identity, never persist an unbound selector as an interaction. */
export function bindToolComponent<T extends TargetedAction>(action: T, sightmap?: SightmapDoc): T {
  const bound = bindActionComponent(action, sightmap)
  if (!action.condition) return bound
  const bind = (condition: JourneyCondition): JourneyCondition => {
    if (condition.type === 'route') return condition
    if (condition.type === 'not') return { ...condition, condition: bind(condition.condition) }
    if ('conditions' in condition) return { ...condition, conditions: condition.conditions.map(bind) }
    const component = condition.type === 'component'
      ? storedReference(condition.component, sightmap, countsInstances(condition.operator))
      : condition.selector === action.selector && bound.component?.path
        ? bound.component
        : bindActionComponent<TargetedAction>({ type: 'click', action: 'Bind condition target', selector: condition.selector }, sightmap).component
    if (!component) throw new Error('Condition requires a Sightmap target')
    return AUTHORED_CONDITION.parse({ type: 'component', operator: condition.operator, component: durableTarget(component) })
  }
  return { ...bound, condition: bind(action.condition) }
}

/** The form a binding persists: the map's canonical reference. A target the map cannot resolve, or that carries a
 *  key resolution stales for its role, fails the binding. */
function storedReference(target: ComponentTarget, sightmap: SightmapDoc | undefined, collection: boolean): ComponentTarget {
  if (!sightmap) return target
  const resolution = resolveDefinition(target, sightmap)
  if (resolution.kind !== 'unique') throw new Error(describeMiss(resolution))
  const keyStale = nonIdentityKeyStale(resolution.hit, collection)
  if (keyStale) throw new Error(describeMiss(keyStale))
  return resolution.hit.reference
}

function bindActionComponent<T extends TargetedAction>(action: T, sightmap?: SightmapDoc): T {
  // In-run anchors are facts about one run's observation, never shared identity.
  const { inRunAnchors: _inRunAnchors, ...shared } = action
  action = shared as T
  if (action.component) action = { ...action, component: durableTarget(action.component) }
  if ('checks' in action && Array.isArray(action.checks)) {
    action = { ...action, checks: action.checks.map(check => {
      if (check && typeof check === 'object' && 'component' in check && check.component) {
        const { selector: _selector, label: _label, ...rest } = check
        return { ...rest, component: durableTarget(storedReference(COMPONENT_TARGET.parse(check.component), sightmap, countsInstances(check.assertion))) }
      }
      return check
    }) }
  }
  if (action.destination) {
    const { value: _value, ...rest } = action
    action = { ...rest, destination: durableTarget(storedReference(action.destination, sightmap, false)) } as T
  }
  if (!needsComponent(action)) return action
  if (action.component) action = { ...action, component: storedReference(action.component, sightmap, false) }
  if (action.component?.path) {
    const { selector: _selector, label: _label, controlId: _controlId, ...rest } = action
    return { ...rest, component: durableTarget(action.component), surfaceId: action.component.surfaceId } as T
  }
  if (action.component && !sightmap) return { ...action, surfaceId: action.component.surfaceId, controlId: action.component.controlId }
  if (!sightmap && action.surfaceId && action.controlId) return { ...action, component: { surfaceId: action.surfaceId, controlId: action.controlId } }
  if (!sightmap) throw new Error(missingActionTarget(action)!)
  const targets = flatTargets(action, sightmap)
  const resolved = flatTargetDefinitions(sightmap, targets)
  if (resolved.length !== 1) {
    const single = targets.length === 1 ? resolveDefinition(targets[0]!, sightmap) : undefined
    throw new Error(`Cannot author tool action "${action.action}": ${single?.kind === 'stale' ? describeMiss(single) : `target must identify exactly one Sightmap component (found ${resolved.length}).`}`)
  }
  const { reference } = resolved[0]!
  const { selector: _selector, label: _label, ...rest } = action
  return { ...rest, surfaceId: reference.surfaceId, component: reference } as T
}

/** Flat targets an action without a component names: its recorded control, or the view components its selector names. */
function flatTargets(action: TargetedAction, sightmap: SightmapDoc): ComponentTarget[] {
  if (action.controlId) return flatTargetCandidates(sightmap, action.controlId, action.surfaceId)
  const selector = action.selector
  if (!selector) return []
  return sightmap.views.filter(view => !action.surfaceId || viewMatchesSurfaceId(view, action.surfaceId)).flatMap(view => flattenComponents(view.components)
    .flatMap(component => view.id && component.id && selectorsOf(component).includes(selector) ? [{ surfaceId: view.id, controlId: component.id }] : []))
}

/** Fresh element checks must already name the observed Sightmap instance. Historical catalogs
 * remain readable; this boundary applies before new authoring is judged or persisted. */
export function bindAuthoredCheck<T extends string | JourneyCheck>(check: T): T {
  if (typeof check === 'string' || UNTARGETED_CHECKS.has(check.assertion)) return check
  const structured: JourneyCheck = check
  const problem = missingCheckTarget(structured)
  if (problem) throw new Error(problem)
  const { selector: _selector, label: _label, surfaceId: _surfaceId, controlId: _controlId, ...rest } = structured
  return { ...rest, component: SIGHTMAP_TARGET.parse(structured.component) } as T
}

/** The authoring boundary for one new action: an authored condition and Sightmap-bound checks. */
export function bindAuthoredAction<T extends { condition?: unknown; checks: Array<string | JourneyCheck> }>(action: T): T {
  return { ...action, ...(action.condition ? { condition: AUTHORED_CONDITION.parse(action.condition) } : {}), checks: action.checks.map(bindAuthoredCheck) }
}
